import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useEstimators } from '../hooks/useEstimators';
import { useAssignLead } from '../hooks/useAssignLead';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';

interface AssignLeadModalProps {
  leadId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal for assigning a lead to an estimator
 * Allows selecting an estimator and optionally overriding their commission rate
 */
export function AssignLeadModal({
  leadId,
  isOpen,
  onClose,
  onSuccess,
}: AssignLeadModalProps) {
  const [selectedEstimatorId, setSelectedEstimatorId] = useState<string>('');
  const [commissionOverride, setCommissionOverride] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  const { estimators, isLoading: isLoadingEstimators, error: estimatorsError } = useEstimators();
  const { assignLead, isLoading: isSubmitting, error: submitError } = useAssignLead();
  const { hasPermission } = useAuth();
  const canViewCommission = hasPermission(Permissions.CommissionsRead);

  // Find the selected estimator to display their default commission rate
  const selectedEstimator = estimators.find(
    (e) => e.id.toString() === selectedEstimatorId
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setSelectedEstimatorId('');
        setCommissionOverride('');
        setValidationError('');
      }, 0);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    // Validate estimator selection
    if (!selectedEstimatorId) {
      setValidationError('Please select an estimator');
      return;
    }

    // Validate commission override if provided
    let commissionValue: number | undefined = undefined;
    if (commissionOverride.trim()) {
      const parsed = parseFloat(commissionOverride);
      if (isNaN(parsed) || parsed < 0 || parsed > 100) {
        setValidationError('Commission override must be between 0 and 100');
        return;
      }
      commissionValue = parsed;
    }

    try {
      await assignLead(leadId, parseInt(selectedEstimatorId, 10), commissionValue);
      onSuccess();
      onClose();
    } catch {
      // Error is handled by the hook and displayed below
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Lead</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <ErrorAlert error={validationError || submitError || estimatorsError} />

          {/* Estimator Selection */}
          <div className="space-y-2">
            <Label htmlFor="estimator">Estimator *</Label>
            <Select
              id="estimator"
              value={selectedEstimatorId}
              onChange={(e) => setSelectedEstimatorId(e.target.value)}
              disabled={isLoadingEstimators || isSubmitting}
            >
              <option value="">Select an estimator</option>
              {estimators.map((estimator) => (
                <option key={estimator.id} value={estimator.id}>
                  {estimator.firstName} {estimator.lastName}
                  {canViewCommission && ` (${estimator.commissionRate}%)`}
                </option>
              ))}
            </Select>
          </div>

          {/* Default Commission Rate Display - Only visible to users with commission read permission */}
          {canViewCommission && selectedEstimator && (
            <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800 border border-blue-200">
              Default commission rate: {selectedEstimator.commissionRate}%
            </div>
          )}

          {/* Commission Override - Only visible to users with commission read permission */}
          {canViewCommission && (
            <div className="space-y-2">
              <Label htmlFor="commissionOverride">Commission Rate Override (%)</Label>
              <Input
                id="commissionOverride"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="Leave blank to use default rate"
                value={commissionOverride}
                onChange={(e) => setCommissionOverride(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-sm text-gray-600">
                Leave blank to use estimator&apos;s default rate
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="action" disabled={isSubmitting || isLoadingEstimators}>
              {isSubmitting ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
